import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { Map, fromJS, List } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import config from '../../config';
import { checkPhone, formatPhone } from '../services/phoneRules';
import checkZip from '../services/zipRules';
import {
  handleGetSuggestion,
  handleUpdateParentCompany,
  closeValidationError,
  changeField,
  saveCompany,
  findDirectors,
  findAnalyst,
  checkCompanyByWebsite,
  revertFields as resetCompanyFields,
} from '../../actions/company/targetCreate';
import { resetBusinessModels } from '../../actions/company/companyTarget';
import { reset as resetTags } from '../../actions/industryTag';
import Popups from '../../components/Popup';
import CountryDropDown from '../CountryDropDown';
import AutoCompleteContainer from '../AutoComplete';
import { getChanged, unwrap } from '../../utils/ChangeSpy';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';
import { usePopupContext } from '../../contexts/PopupContext';

const handleGetSuggestionValue = suggestion => suggestion.legalName;
const handleRenderSuggestion = suggestion => <div>{suggestion.legalName}</div>;

let closable = false;

const AddCompanyTargetPopupContainer = ({
  children,
  popup,
  company,
  targetCompany,
  states,
  entityTypes,
  isValid,
  businessModels,
  tags,
  inputErrors,
  userRole,
  user,
  handleGetSuggestion,
  handleUpdateParentCompany,
  closeValidationError,
  changeField,
  saveCompany,
  findDirectors,
  findAnalyst,
  checkCompanyByWebsite,
  resetCompanyFields,
  resetTags,
  resetBusinessModels,
}) => {
  const redirectForce = useRedirectForceContext();
  const { openPopup, onClosePopup, safeClosePopup: closePopup } = usePopupContext();
  const fromPositionPopup = useRef(popup.getIn(['props', 'fromPositionPopup']));

  if (fromPositionPopup.current) {
    closable = true;
    onClosePopup(popup.getIn(['props', 'cb']));
  } else {
    closable = false;
  }

  const [formError, setFormError] = useState({
    phone: '',
    zip: '',
    industryCategory: '',
    businessModel: '',
    parentCompany: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      changeField({
        name: 'directorName',
        value: user.get('module') || '',
      });
      changeField({
        name: 'recordOwnerId',
        value: user.get('moduleId') || null,
      });

      const roles = user.get('roles', List());

      if (roles.size && roles.getIn([0, 'slug']) === config.ANALYST_ASSOCIATE) {
        changeField({
          name: 'analystName',
          value: user.get('userName'),
        });
        changeField({
          name: 'recordSubOwnerId',
          value: user.get('id'),
        });
      }
    }
  }, []);

  const clearFormError = useCallback(() => {
    setFormError({
      phone: '',
      zip: '',
      businessModel: '',
      industryCategory: '',
      parentCompany: '',
    });
  }, []);

  const showPhoneError = useCallback(() => {
    setFormError(prev => ({
      ...prev,
      phone:
        'Phone must be a valid North American area code and format (###-###-####) or +[numbers, spaces and dashes] if international',
    }));
  }, []);

  const showZipError = useCallback(() => {
    setFormError(prev => ({
      ...prev,
      zip:
        'Zip code should follow U.S. (XXXXX or XXXXX-XXXX) or Canadian (A1A 1A1) format; otherwise, please set address to International ("...")',
    }));
  }, []);

  const showParentCompanyError = useCallback(() => {
    setFormError(prev => ({
      ...prev,
      parentCompany:
        'Must select an existing Parent Profile. If one does not exist, then Parent Company must be profiled.',
    }));
  }, []);

  const isValidPhone = useCallback(
    phone => {
      if (company.get('showCountry', false)) return true;

      if (!checkPhone(phone)) {
        showPhoneError();

        return false;
      }

      return true;
    },
    [company, showPhoneError],
  );

  const isValidZip = useCallback(
    zip => {
      if (!checkZip(zip)) {
        showZipError();

        return false;
      }

      return true;
    },
    [showZipError],
  );

  const isValidIndustryCategory = useCallback((ids, businessModelIds) => {
    let result = true;

    if (!ids.length) {
      setFormError(prev => ({ ...prev, industryCategory: 'Industry Tags is required' }));
      result = false;
    }

    if (!businessModelIds.length) {
      setFormError(prev => ({ ...prev, businessModel: 'Business Model is required' }));
      result = false;
    }

    return result;
  }, []);

  const getIndustryTagIds = useCallback(tagList => {
    const result = [];

    tagList.forEach(t => {
      if (t.get('checked')) {
        result.push(t.get('id'));
      }
      t.get('children').forEach(c => {
        if (c.get('checked')) {
          result.push(c.get('id'));
        }
      });
    });

    return result;
  }, []);

  const getBusinessModelIds = useCallback(models => {
    const result = [];

    models.forEach(t => {
      if (t.get('checked')) {
        result.push(t.get('id'));
      }
    });

    return result;
  }, []);

  const handleSaveCompany = useCallback(
    (body, industryCategoryIds, businessModelIds, afterSuccess, afterError) =>
      saveCompany({
        body: {
          ...body,
          parentId: body.parentId || null,
          industryCategoryIds: industryCategoryIds.concat(businessModelIds),
        },
        afterSuccess,
        afterError,
      }),
    [saveCompany],
  );

  const handleParentCompanyBlur = useCallback(() => {
    clearFormError();

    const selectedCompany = targetCompany.get('parentCompany').get('selected');

    if (!selectedCompany) {
      handleUpdateParentCompany({ text: '' });
      showParentCompanyError();
    }
  }, [targetCompany, handleUpdateParentCompany, clearFormError, showParentCompanyError]);

  const isParentCompanyValid = useCallback(() => {
    const companyType = unwrap(company.get('companyType')) || '';
    const parentCompany = targetCompany.get('parentCompany').get('selected');

    if (!companyType.startsWith('Div') || parentCompany) return true;

    handleUpdateParentCompany({ text: '' });
    showParentCompanyError();

    return false;
  }, [company, targetCompany, handleUpdateParentCompany, showParentCompanyError]);

  const handleCreate = useCallback(
    event => {
      event.preventDefault();
      if (!submitting) {
        const phone = unwrap(company.get('phone', ''));
        const web = unwrap(company.get('web', ''));
        const zip = unwrap(company.get('zip', ''));
        const industryCategoryIds = getIndustryTagIds(tags);
        const businessModelIds = getBusinessModelIds(businessModels);
        const role = userRole.get('slug');
        const isIndustryCategoryValid = isValidIndustryCategory(industryCategoryIds, businessModelIds);
        const isParentValid = isParentCompanyValid();

        if (isValidPhone(phone) && isValidZip(zip) && isIndustryCategoryValid && isParentValid) {
          clearFormError();

          const body = getChanged(company);

          if (body.country && body.country.countryId) {
            body.countryId = body.country.countryId;
            delete body.country;
          }

          let afterSuccess = ({ response }) => {
            setTimeout(() => {
              redirectForce(`/company/${response.id}`);
            }, 0);
          };

          let afterError = () => {
            setSubmitting(false);
          };

          if (fromPositionPopup.current) {
            afterSuccess = () => closePopup();
            afterError = () => closePopup();
          }

          setSubmitting(true);

          const afterCheckSuccess = ({ response: { isExist } }) => {
            if (isExist) {
              openPopup('ConfirmPopup', {
                message: 'This profile appears to be a duplicate. Proceed?',
                onOk: () => handleSaveCompany(body, industryCategoryIds, businessModelIds, afterSuccess, afterError),
                onCancel: () => {
                  resetCompanyFields();
                  resetBusinessModels();
                  resetTags();
                  closePopup();
                },
                yes: 'Continue',
                no: 'Cancel',
              });
            } else {
              handleSaveCompany(body, industryCategoryIds, businessModelIds, afterSuccess, afterError);
            }
          };

          if (role === 'researcher' && web.length > 0) {
            checkCompanyByWebsite(web, afterCheckSuccess);
          } else {
            handleSaveCompany(body, industryCategoryIds, businessModelIds, afterSuccess, afterError);
          }
        }
      }
    },
    [
      submitting,
      company,
      tags,
      businessModels,
      userRole,
      isValidPhone,
      isValidZip,
      isValidIndustryCategory,
      isParentCompanyValid,
      clearFormError,
      handleSaveCompany,
      checkCompanyByWebsite,
      resetCompanyFields,
      resetBusinessModels,
      resetTags,
      openPopup,
      closePopup,
      redirectForce,
    ],
  );

  const handleChange = useCallback(
    event => {
      const {
        target: { name, value },
      } = event;

      if (!name || name === 'parentCompany') return;
      if (name === 'companyType') {
        const typeHasParent = ['DivPri', 'DivLBO', 'DivPub', 'DivVC', 'DivNFP', 'DivFor'];

        setFormError(prev => ({ ...prev, parentCompany: '' }));

        if (typeHasParent.indexOf(value) === -1) {
          handleUpdateParentCompany({ text: '', selected: null });
          changeField({ name: 'parentId', value: null });
        } else {
          changeField({ name: 'parentId', value: 0 });
        }
      }
      changeField({ name, value });
    },
    [changeField, handleUpdateParentCompany],
  );

  const handleToggleCountry = useCallback(() => {
    const currentValue = company.get('showCountry');

    if (currentValue) {
      changeField({ name: 'country.countryId', value: null });
      changeField({ name: 'country.countryName', value: '' });
      changeField({ name: 'showCountry', value: false });
    } else {
      clearFormError();
      changeField({ name: 'showCountry', value: true });
    }
  }, [company, changeField, clearFormError]);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleSuggestChange = useCallback(({ name, value }) => changeField({ name, value }), [changeField]);

  const handleErrorClose = useCallback(
    (event, ...rest) => {
      if (rest[0] === 'zip') {
        setFormError(prev => ({ ...prev, zip: '' }));
      } else {
        closeValidationError({ field: rest[0] });
      }
    },
    [closeValidationError],
  );

  const handleToggleDBA = useCallback(() => {
    const currentValue = targetCompany.getIn(['showDBA']);

    if (typeof handleUpdateCompanyInfo === 'function') {
      // eslint-disable-next-line no-undef
      handleUpdateCompanyInfo({ showDBA: !currentValue });
    }
  }, [targetCompany]);

  const handleParentCompanyClick = useCallback(() => {
    const selectedCompany = targetCompany.get('parentCompany').get('selected');

    if (selectedCompany) {
      window.open(`/company/${selectedCompany.get('id')}`);
    }
  }, [targetCompany]);

  const handleUpdateSuggestions = useCallback(
    update => {
      handleUpdateParentCompany(update);
    },
    [handleUpdateParentCompany],
  );

  const handleGetSuggestionFetch = useCallback(
    ({ value }) => {
      handleGetSuggestion(value);
    },
    [handleGetSuggestion],
  );

  const handleSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      clearFormError();
      handleUpdateParentCompany({
        text: suggestion.legalName,
        selected: suggestion,
        suggestions: [],
      });
    },
    [clearFormError, handleUpdateParentCompany],
  );

  const handleChangeCountryName = useCallback(
    event => {
      changeField({ name: 'country.countryName', value: event.target.value });
    },
    [changeField],
  );

  const handleSelectCountry = useCallback(
    selection => {
      changeField({ name: 'country.countryId', value: selection.id });
      changeField({ name: 'country.countryName', value: selection.text });
    },
    [changeField],
  );

  const handleBlur = useCallback(
    ({ target: { name, value = '' } }) => {
      if (company.get('showCountry', false)) {
        clearFormError();

        return;
      }

      if (name === 'phone') {
        if (isValidPhone(value)) {
          clearFormError();
          changeField({
            name: 'phone',
            value: value ? formatPhone(value) : '',
          });
        } else {
          showPhoneError();
        }
      } else if (name === 'zip' && !checkZip(value)) {
        showZipError();
      }
    },
    [company, clearFormError, isValidPhone, changeField, showPhoneError, showZipError],
  );

  const handleClick = useCallback(() => {
    openPopup('TagsManagementPopup', {
      addNew: true,
      clientView: true,
      cb: () =>
        openPopup('AddCompanyTargetPopup', {
          fromPositionPopup: fromPositionPopup.current,
          cb: popup.getIn(['props', 'cb']),
        }),
    });
  }, [openPopup, popup]);

  const suggestDirector = (
    <AutoCompleteContainer
      idName="recordOwnerId"
      inputProps={{ name: 'directorName' }}
      name="directorName"
      onChange={handleSuggestChange}
      onFind={options => {
        findDirectors({ ...options, field: 'directorName' });
      }}
      rootPath={['targetCompany', 'targetCreate']}
      suggestionsName="directorSuggests"
      valueName="directorName"
    />
  );

  const suggestAnalyst = (
    <AutoCompleteContainer
      idName="recordSubOwnerId"
      inputProps={{ name: 'analystName' }}
      name="analystName"
      onChange={handleSuggestChange}
      onFind={options => {
        findAnalyst({ ...options, field: 'analystName' });
      }}
      rootPath={['targetCompany', 'targetCreate']}
      suggestionsName="analystSuggests"
      valueName="analystName"
    />
  );

  const suggestions = company.getIn(['parentCompany', 'suggestions'], fromJS([]));
  const text = company.getIn(['parentCompany', 'text']) || '';

  const companyInfoProps = {
    onToggleDBA: handleToggleDBA,
    onToggleCountry: handleToggleCountry,
    onParentCompanyClick: handleParentCompanyClick,
  };

  const suggestionProps = {
    name: 'parentCompany',
    inputProps: {
      className: 'parentCompany form-control',
      id: 'parentCompany',
      name: 'parentCompany',
      disabled: false,
      onBlur: handleParentCompanyBlur,
    },
    error: formError.parentCompany,
    canDisplayError: true,
    onErrorClose: clearFormError,
    suggestions,
    onUpdateSuggestions: handleUpdateSuggestions,
    getSuggestion: handleGetSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleSuggestionSelected,
    checkOn: 'text',
    text,
    highlightFirst: false,
  };

  const countryComponent = (
    <CountryDropDown
      name="country.countryId"
      onChange={handleChangeCountryName}
      onSelect={handleSelectCountry}
      value={company.getIn(['country', 'countryName'])}
    />
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddCompanyTargetPopup
          {...companyInfoProps}
          businessModels={businessModels}
          company={company}
          countryComponent={countryComponent}
          entityTypes={entityTypes}
          error={{
            phone: formError.phone,
            zip: formError.zip,
            businessModel: formError.businessModel,
            industryCategory: formError.industryCategory,
            parentCompany: formError.parentCompany,
          }}
          fromPositionPopup={fromPositionPopup.current}
          isValid={isValid}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={handleClick}
          onClose={handleClose}
          onCreate={handleCreate}
          onErrorClose={clearFormError}
          onToggleCountry={handleToggleCountry}
          states={states}
          submitting={submitting}
          suggestAnalyst={suggestAnalyst}
          suggestDirector={suggestDirector}
          suggestionProps={suggestionProps}
          tags={tags}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

const defaultErrors = Map();

function mapStateToProps(state, props) {
  return {
    ...props,
    businessModels: unwrap(state.businessModels.get('models', List())),
    inputErrors: state.targetCompany.targetCreate.get('inputErrors', defaultErrors),
    isValid: state.targetCompany.targetCreate.get('isValid', true),
    states: state.targetCompany.targetCreate.get('states'),
    entityTypes: state.targetCompany.targetCreate.get('entityTypes'),
    company: state.targetCompany.targetCreate,
    targetCompany: state.targetCompany.targetCreate,
    target: state.targetCompany.target,
    tags: state.industryTags.get('tags'),
    userRole: state.auth.getIn(['user', 'roles', 0]),
    user: state.auth.get('user', null),
  };
}

export { AddCompanyTargetPopupContainer };

const Export = connect(
  mapStateToProps,
  {
    handleGetSuggestion,
    changeField,
    saveCompany,
    closeValidationError,
    findDirectors,
    findAnalyst,
    handleUpdateParentCompany,
    checkCompanyByWebsite,
    resetCompanyFields,
    resetTags,
    resetBusinessModels,
  },
  mergeProps,
  connectOptions,
)(memo(AddCompanyTargetPopupContainer));

Export.closable = closable;

export default Export;
